import React, { useEffect } from 'react'
import intl from 'react-intl-universal'
import { getSignedValue } from 'actions/files'
import Toggle from 'components/utils/Toggle'

let videoPositions = []

const Video = ({ videoUrl, hdVideoUrl, allowMediaDownload, sessionEditVideo, creationAIVideoContent }: { videoUrl: string, hdVideoUrl: string|null, allowMediaDownload: boolean, sessionEditVideo?: boolean, creationAIVideoContent?: boolean }) => {
  const [quality, setQuality] = React.useState(localStorage.getItem('videoQuality') || 'sd')
  const [isLoading, setIsLoading] = React.useState(true)
  const _videoRef: any = React.useRef()

  useEffect(() => {
    const handleClick = (e) => {
      if (allowMediaDownload) return

      e.preventDefault()
      return false
    }

    const handleProgress = () => {
      if (!_videoRef.current) return

      videoPositions[videoUrl] = _videoRef.current.currentTime
    }

    const handleVideoLoaded = () => {
      setIsLoading(false)
    }

    const handleVideoError = () => {
      setIsLoading(false)
    }

    if (_videoRef.current) {
      _videoRef.current.addEventListener('contextmenu', handleClick)
      _videoRef.current.addEventListener('timeupdate', handleProgress)
      _videoRef.current.addEventListener('loadeddata', handleVideoLoaded)
      _videoRef.current.addEventListener('error', handleVideoError)

      if (videoPositions[videoUrl]) {
        _videoRef.current.currentTime = videoPositions[videoUrl]
        setTimeout(() => {
          _videoRef.current && _videoRef.current.play()
        }, 50)
      }
    }

    if (hdVideoUrl && !hdVideoUrl.includes('video_processing')) {
      localStorage.setItem('videoQuality', 'hd')
      setQuality('hd')
    } else {
      localStorage.setItem('videoQuality', 'sd')
      setQuality('sd')
    }

    return () => {
      if (_videoRef.current) {
        _videoRef.current.removeEventListener('contextmenu', handleClick)
        _videoRef.current.removeEventListener('timeupdate', handleProgress)
        _videoRef.current.removeEventListener('loadeddata', handleVideoLoaded)
        _videoRef.current.removeEventListener('error', handleVideoError)
      }
    }
  }, [allowMediaDownload, videoUrl, hdVideoUrl])

  const switchQuality = () => {
    const q = quality === 'sd' ? 'hd' : 'sd'
    const currentTime = _videoRef.current.currentTime
    localStorage.setItem('videoQuality', q)
    setQuality(q)
    const wasPaused = _videoRef.current.paused
    _videoRef.current.pause()
    _videoRef.current.currentTime = currentTime
    setTimeout(() => {
      _videoRef.current && !wasPaused && _videoRef.current.play()
    }, 50)
  }

  const signedValue = getSignedValue(videoUrl)
  const hdSignedValue = hdVideoUrl ? getSignedValue(hdVideoUrl) : signedValue

  function hasPolicyParameter(url) {
    if (!url || typeof url !== 'string') {
      return false
    }

    try {
      const searchParams = new URLSearchParams(new URL(url).search)
      return searchParams.has("Policy") || url.includes('gnowbe-videos')
    } catch {
      // Invalid URL, fallback to string check
      return url.includes('gnowbe-videos')
    }
  }

  const hasPolicy = hasPolicyParameter(videoUrl)

  return (
    <div className="text-center bg-white relative" onContextMenu={!allowMediaDownload ? (e) => e.preventDefault() : undefined}>
      {creationAIVideoContent ? (
        <div className="skeleton-box w-full bg-gray-200 rounded" style={{ height: sessionEditVideo ? '100%' : 400 }} />
      ) : (
        <>
          {isLoading && hasPolicy && (
            <div className="skeleton-box w-full bg-gray-200 rounded absolute inset-0 z-10" style={{ height: sessionEditVideo ? '100%' : 400 }} />
          )}
          {hasPolicy ?
            <video
              key={quality}
              ref={_videoRef}
              style={{ width: '100%', height: sessionEditVideo ? '100%' : 400, opacity: isLoading ? 0 : 1 }}
              controls
              controlsList={`${allowMediaDownload ? '' : 'nodownload'}`}
            >
              <source src={quality === 'hd' && hdSignedValue ? hdSignedValue : signedValue} />
              {intl.get('video_not_supported')}
            </video>
            :
            <div style={{ width: '100%', height: 400, background: 'black' }}>Loading</div>
          }
        </>
      )}

      {(hdSignedValue && !hdSignedValue.includes('video_processing') && !sessionEditVideo && !creationAIVideoContent) &&
        <Toggle label={quality === 'sd' ? intl.get('video_hq_text') : intl.get('video_lq_text')} id="toggleVideoQuality" onChange={switchQuality} defaultChecked={quality === 'hd'} invertLabel={true} videoQualityToggle={true} />
      }
    </div>
  )
}

export default Video
